import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { findBreadcrumbPath } from "../utils/breadcrumbs";

const MOBILE_BP = 992;

const AppBreadcrumb = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const menuData = useSelector((state) => state.menu.menuData);
  const mode = useSelector((state) => state.ui.mode);
  const userDetails = useSelector((state) => state.User.userDetails);
  const isPlatformUser = userDetails?.orgType === "PLATFORM";

  const modules = (menuData?.modules || [])
    .filter((m) => m.isActive && m.mode === mode)
    .filter((m) => !m.platformOnly || isPlatformUser);

  const pageItems = findBreadcrumbPath(modules, location.pathname);

  // The client being viewed (platform CLIENT_VIEW) is shown by the topbar's
  // client chip, not repeated here.
  const breadcrumbItems = pageItems;

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BP);
  const [expanded, setExpanded] = useState(false);

  // Reset expanded state whenever route changes
  useEffect(() => {
    setExpanded(false);
  }, [location.pathname]);

  // Track viewport
  useEffect(() => {
    const handler = () => {
      const mobile = window.innerWidth < MOBILE_BP;
      setIsMobile(mobile);
      if (!mobile) setExpanded(false);
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const handleClick = (item) => {
    if (item.type === "module") {
      dispatch({ type: "SET_SIDEBAR_COLLAPSED", payload: false });
      dispatch({ type: "SET_ACTIVE_PANEL_KEY", payload: item.key });
      dispatch({ type: "SET_SIDEBAR_OPEN_KEYS", payload: [] });
    }
    if (item.type === "submodule") {
      const moduleItem = breadcrumbItems.find((x) => x.type === "module");
      dispatch({ type: "SET_SIDEBAR_COLLAPSED", payload: false });
      if (moduleItem) {
        dispatch({ type: "SET_ACTIVE_PANEL_KEY", payload: moduleItem.key });
      }
      dispatch({ type: "SET_SIDEBAR_OPEN_KEYS", payload: [item.key] });
    }
    if (item.type === "page") {
      navigate(item.route);
    }
  };

  if (!breadcrumbItems.length) return null;

  const hasCollapsed = isMobile && breadcrumbItems.length > 1;
  // On mobile collapsed: hide all items except the last one
  const showCollapsedEllipsis = hasCollapsed && !expanded;

  return (
    <nav className="app-bc" aria-label="Breadcrumb">
      {/* Ellipsis toggle — only on mobile when more than 1 item */}
      {hasCollapsed && (
        <button
          className="app-bc-toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-label={expanded ? "Collapse breadcrumb" : "Expand breadcrumb"}
        >
          <span
            className="app-bc-chevron"
            style={{ transform: expanded ? "rotate(90deg)" : "rotate(0deg)" }}
          >
            ›
          </span>
        </button>
      )}

      <ol className="app-bc-list">
        {breadcrumbItems.map((item, index) => {
          const isLast = index === breadcrumbItems.length - 1;
          const hidden = showCollapsedEllipsis && !isLast;

          return (
            <li
              key={index}
              className={`app-bc-item${hidden ? " app-bc-item--hidden" : ""}`}
            >
              {/* Separator — not before the first visible item */}
              {index > 0 && (
                <span className="app-bc-sep" aria-hidden="true"> / </span>
              )}
              <span
                className={`app-bc-label${isLast ? " app-bc-label--active" : ""}`}
                onClick={() => !isLast && handleClick(item)}
                role={!isLast ? "button" : undefined}
                tabIndex={!isLast ? 0 : undefined}
                onKeyDown={
                  !isLast
                    ? (e) => e.key === "Enter" && handleClick(item)
                    : undefined
                }
              >
                {item.label}
              </span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export default AppBreadcrumb;
